import { useTheme, Box, InputBase } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { SearchRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';
import { searchResultMap } from '../exo/search/searchResultsMap';

// pause in typing before searching
const SEARCH_DEBOUNCE_MS = 200;
// Recent results, shared by all instances: the recipient field mounts a new
// search every time it is focused, which re-sent the same request each time.
const CACHE_TTL_MS = 60 * 1000;
const CACHE_MAX_ENTRIES = 50;
const searchCache = new Map();

function readCache(key) {
  const entry = searchCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.time > CACHE_TTL_MS) {
    searchCache.delete(key);
    return null;
  }
  return entry.response;
}

function writeCache(key, response) {
  searchCache.set(key, { time: Date.now(), response });
  if (searchCache.size > CACHE_MAX_ENTRIES)
    searchCache.delete(searchCache.keys().next().value);
}

const DBMultiSearch = ({
  params,
  onSearch,
  noInitialLoad,
  sort = null,
  children,
  limit = 25,
  sx,
  value,
  hideSearchBar,
  className,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const scrollContainerRef = useRef(null);

  const cookies = new Cookies();
  const prefix = cookies.get('searchPrefix');

  const [search, setSearch] = useState('');
  const [searchOffset, setSearchOffset] = useState(0);
  const [hits, setHits] = useState([]);

  // only the latest request may show its results
  const requestIdRef = useRef(0);
  const debounceRef = useRef(null);
  const hasSearchedRef = useRef(false);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  useEffect(() => {
    if (noInitialLoad || !config) return;
    const run = () => handleSearch({ target: { value: value || '' } });
    // the first search runs right away, later ones once typing pauses
    if (!hasSearchedRef.current) {
      hasSearchedRef.current = true;
      run();
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(run, SEARCH_DEBOUNCE_MS);
  }, [config, value]);

  // typing in the own search bar
  function handleInputChange(event) {
    const nextValue = event.target.value;
    setSearch(nextValue);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(
      () => handleSearch({ target: { value: nextValue } }),
      SEARCH_DEBOUNCE_MS,
    );
  }

  function handleSearch(event, newPage, keepHits = false) {
    if (!config) return;
    var searchQueries = [];

    const allowedIndexes = cookies.get('searchIndexes');

    params.forEach((param) => {
      const indexUid = prefix + param;
      if (allowedIndexes.includes(indexUid))
        searchQueries.push({
          indexUid,
          q: event ? event.target.value : search,
          showRankingScore: true,
        });
    });

    const searchData = {
      queries: searchQueries,
    };

    const query = event ? event.target.value : search;
    const requestId = ++requestIdRef.current;
    const cacheKey = config.searchUrl + JSON.stringify(searchData);
    const handleResults = (res) => {
      // a newer search started meanwhile: its results win
      if (requestId !== requestIdRef.current) return;
      prepairHits(res, keepHits, query);
      setSearchOffset(searchOffset + limit);
    };

    const cached = readCache(cacheKey);
    if (cached) {
      handleResults(cached);
    } else {
      SearchRequest({
        baseUrl: config.searchUrl,
        path: `multi-search`,
        method: 'post',
        data: searchData,
        onResponse: (res) => {
          if (res) writeCache(cacheKey, res);
          handleResults(res);
        },
      });
    }
    if (event) setSearch(event.target.value);
  }

  function prepairHits(res, keepHits, query) {
    if (!res) return;
    const { results } = res;
    var totalHitsList = [];
    // get group
    results.forEach((result) => {
      const key = result.indexUid.replace(prefix, '');
      const resultsInfo = searchResultMap[key];

      // add hits with additional infos
      result.hits?.forEach((element) => {
        totalHitsList.push({
          ...element,
          type: resultsInfo.label,
          Icon: resultsInfo.Icon,
          to: resultsInfo.to,
          path: resultsInfo.path,
        });
      });
    });

    if (query?.trim()) {
      // sort hits by relevance score (highest first)
      totalHitsList.sort(
        (a, b) => (b._rankingScore || 0) - (a._rankingScore || 0),
      );
    } else {
      // sort hits alphabetically when no search term is given
      totalHitsList.sort((a, b) =>
        a.label.toUpperCase().localeCompare(b.label.toUpperCase()),
      );
    }

    onSearch(keepHits ? [...hits, ...totalHitsList] : totalHitsList);
    setHits(keepHits ? [...hits, ...totalHitsList] : totalHitsList);
  }

  return (
    <Box
      ref={scrollContainerRef}
      sx={sx}
      className={
        'relative flex flex-col gap-2 h-full w-full overflow-y-auto pr-2 overflow-x-hidden'
      }
    >
      {!hideSearchBar && (
        <Box
          className="flex backdrop-blur-lg py-1 sticky top-0 z-10"
          borderRadius="3px"
          backgroundColor={colors.bgInput}
        >
          <InputBase
            sx={{ ml: 2, flex: 1 }}
            placeholder={t('Search')}
            value={search}
            onChange={handleInputChange}
            className="w-full"
          />
        </Box>
      )}
      {children}
    </Box>
  );
};

export default DBMultiSearch;
